import { difference } from 'lodash'

import { CardType, CategoryType, FORMAT, EDH_FORMATS, FORMAT_IDS, FormatType, DEPRICATED_FORMAT_IDS } from 'types/deck'
import { Color } from 'services/apiTypes/card.types'

import CompanionLookup from './companion'
import { ERRORS, CARD_QTY_OVERRIDE, COMMANDER_OVERRIDE, SINGLE_EXTRA_COLOR_COMMANDERS } from './legality.const'

import CheckCommanderLeglaity from './formats/commander.format'
import OathbreakerLegality from './formats/oathbreaker.format'
import CheckBrawlLegality from './formats/brawl.format'
import CheckHistoricBrawlLegality from './formats/historicBrawl.format'
import RestrictedSixtyCardLegality from './formats/restrictedSixtyCard.format'
import CheckGenericLeglaity from './formats/default.format'
import CheckPauperEDHLegality from './formats/pdh.format'
import CheckGladiatorLegality from './formats/gladiator.format'
import CheckDualCommanderLegality from './formats/dualCommander.format'
import CheckCanadianHighlanderLegality from './formats/canadianHighlander.format'

import { getCardsColorIdenty } from 'utils/cardHelpers'
import { calculateEDHBracketDetails } from 'redux/deck/selectors'

export type LegalityChecker = (deck: DeckLeglaity) => void
export type Errors = {
  errors: string[]
  cardErrors: Record<string, boolean>
  bracketErrors: Record<string, boolean>
  onlyBracketErrors: boolean
}

// Any format not listed here will use the generic legality checkers (split between edh/ non edh formats)
const CustomLegalityCheckers: Record<number, LegalityChecker> = {
  [FORMAT.BRAWL]: CheckBrawlLegality,
  [FORMAT.HISTORIC_BRAWL]: CheckHistoricBrawlLegality,
  [FORMAT.DUEL_COMMANDER]: CheckDualCommanderLegality,
  [FORMAT.PAUPER_EDH]: CheckPauperEDHLegality,
  [FORMAT.OATHBREAKER]: OathbreakerLegality,
  [FORMAT.GLADIATOR]: CheckGladiatorLegality,
  [FORMAT.VINTAGE]: RestrictedSixtyCardLegality,
  [FORMAT.TIMELESS]: RestrictedSixtyCardLegality,
  [FORMAT.CANLANDER]: CheckCanadianHighlanderLegality,
}

const IGNORE_COMPANION: number[] = [FORMAT.GLADIATOR]

/**
 * Given a cardMap, format, and categories of a deck. We can calculate that decks legality and enter any errors it may
 * have into dedicated error objects
 *
 * By default the constructor will calculate the format's default legality, but you can run any extra legality checks on
 * on the object if neccessary after instantiation
 */
export class DeckLeglaity {
  format: FormatType
  cards: Record<string, CardType>
  categories: Record<string, CategoryType>
  edhBracket: number | null

  errors: string[]
  cardErrors: string[]
  bracketErrors: string[]

  mainboard: CardType[]
  sideboard: CardType[]
  commanders: CardType[]

  companions: CardType[]

  cardQuantities: Record<string, number> // dictionary of unique card names to the sum of their quantities (useful if you have 2 copies of a card in the main, and 2 copies of a card in the side)
  deckSize: number // Sum of all quantities (not sideboard)
  sideboardSize: number // Sum of all quantities (just sideboard)

  commandersColorIdent: Color[]

  constructor(
    cards: Record<string, CardType>,
    format: FormatType,
    categories: Record<string, CategoryType>,
    edhBracket: number | null,
  ) {
    this.format = format
    this.cards = cards
    this.categories = categories
    this.edhBracket = edhBracket

    this.errors = new Array<string>()
    this.cardErrors = new Array<string>()
    this.bracketErrors = new Array<string>()

    this.mainboard = new Array<CardType>()
    this.sideboard = new Array<CardType>()
    this.commanders = new Array<CardType>()

    this.companions = new Array<CardType>()

    this.cardQuantities = {}
    this.deckSize = 0
    this.sideboardSize = 0

    const cardIds = Object.keys(cards)

    // Loop over all cards in our map and break them out into their respective boards (commander, main, side)
    // Note that even if a companion isn't marked as included in the deck, we'll still check it's companion legality
    // This is for non sideboard formats where a user may want to not include the companion in the sideboard, but rather a
    // different category that isn't included in the deck
    for (const cardId of cardIds) {
      const card = cards[cardId]

      const primaryCategoryName = card.categories[0]

      const isCommander = categories[primaryCategoryName]?.isPremier || false
      const includInDeck = categories[primaryCategoryName]?.includedInDeck || false

      const isSideboard = primaryCategoryName === 'Sideboard'

      if (card.companion) this.companions.push(card)

      if (!includInDeck) continue

      // Count total quantities of cards by name here
      // We do this irrespective of board since sideboard formats may only have up to 4 copies in any board
      // This also catches if you have multiple copies (records) of the same card in a deck
      if (this.cardQuantities[card.name]) this.cardQuantities[card.name] += card.qty
      else this.cardQuantities[card.name] = card.qty

      if (isSideboard) {
        this.sideboard.push(card)
        this.sideboardSize += card.qty

        continue
      }

      this.deckSize += card.qty

      if (isCommander) this.commanders.push(card)
      else this.mainboard.push(card)
    }

    this.commandersColorIdent = this.getCommanderDeckColorIdent(this.mainboard, this.sideboard, this.commanders)

    if (this.companions.length && !IGNORE_COMPANION.includes(format)) this.checkCompanion()

    if (format === FORMAT.CUSTOM) return // there's no legality checking to do in a custom format

    const customLegalityChecker = CustomLegalityCheckers[format] // may be undefined
    const genericLegalityChecker = EDH_FORMATS.includes(format) ? CheckCommanderLeglaity : CheckGenericLeglaity

    // Actual format legality checking takes place here
    // The legality checkers have access to everything that the class does
    // This way they can add whatever custom checking they want calling any helpers below
    // Or custom code on a per format basis
    if (customLegalityChecker) customLegalityChecker(this)
    else genericLegalityChecker(this)
  }

  /** Error Managers  **/

  addError(error: string) {
    this.errors.push(error)
  }

  addCardError(error: string, card: CardType) {
    this.errors.push(`${error} ${card.name}`)
    this.cardErrors.push(card.name)
  }

  addBracketError(error: string, card: CardType) {
    this.errors.push(`${error} ${card.name}`)
    this.bracketErrors.push(card.name)
  }

  getErrors(): Errors {
    // Returns errors as an object to allow cards to lookup whether they have errors at O(1)
    const buildCardErrors = (acc: Record<string, boolean>, cardName: string) => {
      acc[cardName] = true

      return acc
    }

    const cardErrors = this.cardErrors.reduce(buildCardErrors, {} as Record<string, boolean>)
    const bracketErrors = this.bracketErrors.reduce(buildCardErrors, {} as Record<string, boolean>)

    const onlyBracketErrors = !!this.bracketErrors.length && this.bracketErrors.length === this.errors.length

    return { cardErrors, bracketErrors, errors: this.errors, onlyBracketErrors }
  }

  /** Helpers  **/

  getCardsColorIdentity(cards: CardType[]): Color[] {
    return cards.reduce<Color[]>(getCardsColorIdenty, new Array<Color>())
  }

  getCommanderDeckColorIdent(mainboard: CardType[], sideboard: CardType[], commanders: CardType[]) {
    const allowedExtraColor = commanders.filter(card => SINGLE_EXTRA_COLOR_COMMANDERS.includes(card.name)).length > 0
    const commanderIdent = commanders.reduce<Color[]>(getCardsColorIdenty, new Array<Color>())
    const fullDeckIdentity = [...mainboard, ...sideboard, ...commanders].reduce<Color[]>(
      getCardsColorIdenty,
      new Array<Color>(),
    )
    const colorDiff = difference(fullDeckIdentity, commanderIdent)

    if (!colorDiff.length) return commanderIdent

    if (allowedExtraColor && colorDiff.length === 1) return [...commanderIdent, colorDiff[0]]
    if (allowedExtraColor && colorDiff.length > 1) this.errors.push(ERRORS.ADITIONAL_COLOR_COMMANDER)

    return commanderIdent
  }

  /** WHERE THE LEGALITY CHECKERS START - any logic that's used by more than one format can be added here **/

  checkCardLegality(card: CardType) {
    const formatName = FORMAT_IDS[this.format] || DEPRICATED_FORMAT_IDS[this.format]
    const legality = card.legalities[formatName]

    if (!legality) {
      // TODO - something is mapping cards incorrectly (sometimes it's not an object!)
      // console.error(`BadState error: Failed to find a legality for ${card.name} using format: ${formatName}!`)

      return // just returning to not break the page. This will result in incorrect legality, but the user can continue brewing
    }

    if (legality === 'banned') this.addCardError(ERRORS.BANNED, card)
    else if (legality === 'not_legal' && this.format !== FORMAT.EDH) this.addCardError(ERRORS.NOT_LEGAL, card)
    // Special legality clause to not show the red border on cards pre-release in EDH only
    else if (legality === 'not_legal' && this.format === FORMAT.EDH) {
      const unreleased = new Date(card.releasedAt) > new Date()

      if (
        unreleased &&
        card.setType !== 'funny' &&
        card.setType !== 'memorabilia' &&
        !card.types.includes('Conspiracy') &&
        !card.types.includes('Plane')
      )
        // Note: We're explicitly not using a cardError here to avoid the red highlights
        this.addError(`${ERRORS.PRERELEASED_CARD_EDH} (${card.releasedAt}) - ${card.name}`)
      else this.addCardError(ERRORS.NOT_LEGAL, card)
    }
  }

  checkCardQuantity(card: CardType, formatLimit = 4) {
    let qtyOverridden = false

    const overridenQuantity = CARD_QTY_OVERRIDE[card.name] // Cards that are allowed to have as many as one wants, or a certain amount greater than the format normally limits

    if (overridenQuantity && overridenQuantity < 0) qtyOverridden = true
    else if (overridenQuantity && this.cardQuantities[card.name] <= overridenQuantity) qtyOverridden = true

    if (!qtyOverridden && this.cardQuantities[card.name] > formatLimit)
      this.addCardError(formatLimit === 1 ? ERRORS.SINGLETON : ERRORS.MAX_COUNT, card)
  }

  checkPartners() {
    const commanderOne = this.commanders[0]
    const commanderTwo = this.commanders[1]

    if (!commanderOne || !commanderTwo) return // clearly not a deck with partners in it

    // prettier-ignore
    const areFriendsForever = commanderOne.text.includes('Friends forever') && commanderTwo.text.includes('Friends forever')
    const arePartners = commanderOne.text.includes('Partner') && commanderTwo.text.includes('Partner')
    const areBackgroundPair =
      (commanderOne.text.includes('Choose a Background') || commanderTwo.text.includes('Choose a Background')) &&
      (commanderOne.subTypes.includes('Background') || commanderTwo.subTypes.includes('Background'))

    // prettier-ignore
    const areDoctorAndCompanion =
      (
        (commanderOne.subTypes.includes('Doctor') && commanderOne.subTypes.includes('Time Lord')) ||
        (commanderTwo.subTypes.includes('Doctor') && commanderTwo.subTypes.includes('Time Lord'))
      ) &&
      (commanderOne.text.includes("Doctor's companion") || commanderTwo.text.includes("Doctor's companion"))

    if (areBackgroundPair || areFriendsForever || areDoctorAndCompanion) return // stupid secret lair

    if (!arePartners) this.addError(ERRORS.PARTNER_COMMANDERS)
    else {
      if (commanderOne.text.includes('Partner with')) {
        if (!commanderOne.text.includes(commanderTwo.name)) this.addError(ERRORS.PARTNER_WITH_COMMANDERS)
        else if (!commanderTwo.text.includes(commanderOne.name)) this.addError(ERRORS.PARTNER_WITH_COMMANDERS)
      }
    }
  }

  checkCompanion() {
    if (!this.companions.length) return

    for (const companion of this.companions) {
      const checkCompanion = CompanionLookup[companion.name]

      if (EDH_FORMATS.includes(this.format)) {
        this.checkCardColorIdentAgainstCommanders(companion)

        const existingQuantity = this.cardQuantities[companion.name]

        if (existingQuantity > 1) this.addCardError(ERRORS.SINGLETON, companion)
      }

      // Unknown companion, add a generic error and continue
      if (!checkCompanion) {
        this.addCardError(ERRORS.UNKNOWN_COMPANION, companion)

        continue
      }

      checkCompanion([...this.mainboard, ...this.commanders], this, companion.name)
    }
  }

  checkLegendaryCommander() {
    const commander = this.commanders[0]

    if (!commander) return // returning early here to keep this method doing a single thing. If the commander is missing handle it elsewhere
    if (!commander.superTypes.includes('Legendary')) this.addCardError(ERRORS.LEGENDARY_COMMANDER, commander)
  }

  checkCanBeCommander() {
    const commander = this.commanders[0]

    if (!commander) return // not our problem, handle this elsewhere

    const hasPowerAndToughness =
      (commander.front?.power || commander.power) && (commander.front?.toughness || commander.toughness)

    // Note - can't check legendary here due to PDH
    const canBeCommander =
      (hasPowerAndToughness &&
        (commander.types.includes('Creature') ||
          commander.subTypes.includes('Vehicle') ||
          commander.subTypes.includes('Spacecraft'))) ||
      commander.text.includes('can be your commander') ||
      (commander.subTypes.includes('Background') && this.commanders.length === 2) ||
      COMMANDER_OVERRIDE.includes(commander.name)

    if (!canBeCommander) this.addCardError(ERRORS.CREATURE_COMMANDER, commander)
  }

  checkBannedAsCommander() {
    const commander = this.commanders[0]

    if (!commander) return // not our problem, handle this elsewhere

    const formatName = FORMAT_IDS[this.format] || DEPRICATED_FORMAT_IDS[this.format]
    const legality = commander.legalities[formatName]

    if (legality === 'restricted') this.addCardError(ERRORS.BANNED_AS_COMMANDER, commander)
  }

  checkHasCommander(): boolean {
    const commander = this.commanders[0]

    if (!commander) this.addError(ERRORS.REQUIRE_COMMANDER)

    return !!commander
  }

  checkMaximumCommanders() {
    if (this.commanders.length > 2) this.addError(ERRORS.SINGLE_COMMANDER) // partners are accounted for elsewhere
  }

  checkEDHBracket() {
    // Only EDH formats have brackets
    if (this.format !== FORMAT.EDH) return

    // NO bracket legality checking is done unless they have an EXPLICITLY set bracket
    if (!this.edhBracket) return

    // No deck restrictions for brackets 4/5
    if (this.edhBracket === 4 || this.edhBracket === 5) return

    const { gameChangers, massLandDenial, tutors, extraTurns, atomicCombos } = calculateEDHBracketDetails(
      this.commanders,
      [...this.mainboard, ...this.sideboard],
    )

    if (this.edhBracket === 1) {
      if (gameChangers.length) gameChangers.forEach(card => this.addBracketError(ERRORS.NO_GAME_CHANGERS, card))
      if (massLandDenial.length) massLandDenial.forEach(card => this.addBracketError(ERRORS.NO_MASS_LAND_DENIAL, card))
      if (tutors.length > 3) tutors.forEach(card => this.addBracketError(ERRORS.TOO_MANY_NON_LAND_TUTORS, card))
      if (extraTurns.length) extraTurns.forEach(card => this.addBracketError(ERRORS.NO_EXTRA_TURNS, card))
      if (atomicCombos.length) atomicCombos.forEach(card => this.addBracketError(ERRORS.NO_TWO_CARD_INF_COMBOS, card))
    }

    if (this.edhBracket === 2) {
      if (gameChangers.length) gameChangers.forEach(card => this.addBracketError(ERRORS.NO_GAME_CHANGERS, card))
      if (massLandDenial.length) massLandDenial.forEach(card => this.addBracketError(ERRORS.NO_MASS_LAND_DENIAL, card))
      if (tutors.length > 3) tutors.forEach(card => this.addBracketError(ERRORS.TOO_MANY_NON_LAND_TUTORS, card))
      if (atomicCombos.length) atomicCombos.forEach(card => this.addBracketError(ERRORS.NO_TWO_CARD_INF_COMBOS, card))
    }

    if (this.edhBracket === 3) {
      if (gameChangers.length > 3)
        gameChangers.forEach(card => this.addBracketError(ERRORS.TOO_MANY_GAME_CHANGERS, card))
      if (massLandDenial.length) massLandDenial.forEach(card => this.addBracketError(ERRORS.NO_MASS_LAND_DENIAL, card))
    }
  }

  checkCardColorIdentAgainstCommanders(card: CardType) {
    const colorDiff = difference(card.colorIdentity, this.commandersColorIdent)

    if (colorDiff.length) this.addCardError(ERRORS.OUTSIDE_COLOR_IDENTITY, card)
  }
}

export default DeckLeglaity
